import { useState, useCallback, useMemo } from 'react';
import { invokeAsExtension } from '../../extensions/capabilities/enforce';
import { clearDbManagerTabNeedsReconnect } from '../../store/tabsStore';
import { getDbQueryResult, setDbQueryResult } from '../../store/dbQueryResultCache';
import {
  isDbConnectionLive,
  readSavedDbConnection,
  connectDbFromSavedConfig,
  DB_PASSWORD_REQUIRED,
} from '../../extensions/capabilities/db';
import {
  DB_QUERY_COMMANDS,
  quoteIdentFor,
  makePlaceholder,
  listTables,
  listColumns,
  DB_CONNECT_COMMANDS,
  DB_ENGINE_LABELS,
} from '../../extensions/dbEngines';
import { makeColumnCaster } from '../dbGridSql';
import { setDbConnectionPassword } from '../../extensions/capabilities/dbVaultConnections';
import { filterWhereSql } from '../dbGridFilterClause';
import { orderTerms } from '../dbGridSort';
import { resultToCsv, resultToJson, resultToMarkdown, resultToHtmlTable } from '../dbExportUtils';
import { MAX_RICH_COPY_ROWS, engineOf, tableRefFor } from './dbResultsTabHelpers';

/** Copy, result-set switch, export fetch, schema lookup and reconnect for the results tab. */
export function useDbResultsActions({
  tab,
  result,
  setResult,
  setError,
  setFailedSql,
  primaryKeyCols,
  tableColumns,
  sortRef,
  setSelectedRows,
  setSelectedCells,
  resultSets,
  setResultSetIndex,
  setPasswordPrompt,
}) {
  const [reconnecting, setReconnecting] = useState(false);
  const [copiedFormat, setCopiedFormat] = useState(null); // 'csv' | 'json' | null

  const COPY_SERIALIZERS = {
    csv: resultToCsv,
    json: resultToJson,
    md: resultToMarkdown,
    html: resultToHtmlTable,
  };

  const copyResult = (format) => {
    if (!result || !result.columns?.length) return;
    const serialize = COPY_SERIALIZERS[format] || resultToCsv;
    // Markdown/HTML are for pasting somewhere a human reads, so a whole
    // 10k-row grid is never the intent — and building that string on the UI
    // thread would freeze the window. CSV/JSON stay uncapped: those are data.
    const capped = format === 'md' || format === 'html';
    const rows = capped ? result.rows.slice(0, MAX_RICH_COPY_ROWS) : result.rows;
    const text = serialize({ ...result, rows });
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopiedFormat(format);
        setTimeout(() => setCopiedFormat(null), 1500);
      })
      .catch(() => {});
  };

  // Result-set switcher (query tabs, multi-statement scripts): swaps the
  // grid to another entry of the cached array and remembers the position so
  // a tab switch restores the same view.
  const selectResultSet = useCallback((i) => {
    if (!tab || !resultSets || !resultSets[i]) return;
    setResultSetIndex(i);
    setResult(resultSets[i]);
    setSelectedRows(new Set()); // indexes of another result set
    setSelectedCells(new Set());
    const cached = getDbQueryResult(tab.id);
    if (cached?.results) setDbQueryResult(tab.id, { ...cached, selectedIndex: i });
  }, [tab, resultSets, setResultSetIndex, setResult, setSelectedRows, setSelectedCells]);

  // "Entire table" export: the modal pages through the table client-side with
  // the exact SELECT the grid uses (same table ref, committed WHERE filter,
  // active sort). Chunk boundaries are only meaningful over a TOTAL order —
  // an OFFSET page of an unordered (or tie-splitting) SELECT can silently
  // duplicate and drop rows (postgres synchronize_seqscans, plan changes,
  // concurrent writes) — so the ORDER BY always ends with the primary key
  // (sqlite: rowid when no PK is declared). Unsorted single-column-PK tables
  // page by keyset (pk > cursor) instead of OFFSET: stable under concurrent
  // writes and O(N) server-side instead of O(N²/chunk).
  //
  // Contract with DbExportModal: fetchChunk(cursor, limit) -> { columns,
  // rows, nextCursor }. cursor is opaque to the modal (null on the first
  // call; nextCursor === null means done). limit == null asks for the whole
  // (filtered) table in ONE query — the modal uses that when exportMeta says
  // chunking is unsafe (no PK on postgres/mysql: ctid moves on UPDATE and
  // ORDER BY every column trips types without ordering operators, so a
  // single-statement snapshot is the only deterministic option).
  const exportFetchChunk = useCallback(async (cursor, limit) => {
    const engine = engineOf(tab);
    const base = `SELECT * FROM ${tableRefFor(engine, tab)}`;
    const filter = filterWhereSql(tab.filterText).replace(/^ WHERE /, '');
    const levels = sortRef.current;

    const orderCols = orderTerms(engine, levels);
    for (const c of primaryKeyCols) {
      if (!levels.some((l) => l.col === c)) orderCols.push(quoteIdentFor(engine, c));
    }
    // A no-PK sqlite table can't be WITHOUT ROWID (that clause requires a
    // PK), so rowid always exists here.
    if (orderCols.length === 0 && engine === 'sqlite') orderCols.push('rowid');
    const invokeArgs = (sql, params = []) =>
      invokeAsExtension(DB_QUERY_COMMANDS[engine], { extensionId: tab.extensionId, connId: tab.connId, sql, params });

    if (limit == null) {
      const where = filter ? ` WHERE ${filter}` : '';
      const order = orderCols.length ? ` ORDER BY ${orderCols.join(', ')}` : '';
      const res = await invokeArgs(`${base}${where}${order}`);
      return { ...res, nextCursor: null };
    }

    if (levels.length === 0 && primaryKeyCols.length === 1) {
      // Keyset page. The cursor param gets the same postgres cast treatment
      // as the grid's write path (bound text params don't coerce).
      const pk = primaryKeyCols[0];
      const ph = makePlaceholder(engine);
      const castFor = makeColumnCaster(engine, tableColumns);
      const conds = [];
      const params = [];
      if (filter) conds.push(`(${filter})`);
      if (cursor && typeof cursor === 'object') {
        conds.push(`${quoteIdentFor(engine, pk)} > ${ph()}${castFor(pk)}`);
        params.push(cursor.value);
      }
      const where = conds.length ? ` WHERE ${conds.join(' AND ')}` : '';
      const res = await invokeArgs(`${base}${where} ORDER BY ${quoteIdentFor(engine, pk)} LIMIT ${limit}`, params);
      let nextCursor = null;
      if (res.rows.length === limit) {
        const pkIdx = res.columns.indexOf(pk);
        const last = pkIdx === -1 ? null : res.rows[res.rows.length - 1]?.[pkIdx];
        if (last != null) nextCursor = { value: last };
      }
      return { ...res, nextCursor };
    }

    // Ordered OFFSET paging (composite PK, sqlite rowid fallback, or a user
    // sort with PK tiebreakers). Correct because the order is total; slower
    // than keyset on huge tables.
    const offset = typeof cursor === 'number' ? cursor : 0;
    const where = filter ? ` WHERE ${filter}` : '';
    const sql = `${base}${where} ORDER BY ${orderCols.join(', ')} LIMIT ${limit}${offset > 0 ? ` OFFSET ${offset}` : ''}`;
    const res = await invokeArgs(sql);
    return { ...res, nextCursor: res.rows.length === limit ? offset + res.rows.length : null };
  }, [tab, primaryKeyCols, tableColumns, sortRef]);

  // Table/column names for the SQL editor's completions, resolved lazily from
  // the shared per-connection schema catalog (cached; invalidated on connect).
  const querySchema = useMemo(() => {
    if (!tab) return null;
    const engine = engineOf(tab);
    const { extensionId, connId, dbName } = tab;
    return {
      listTables: () => listTables(engine, extensionId, connId, dbName),
      listColumns: (table) => listColumns(engine, extensionId, connId, dbName, table),
    };
  }, [tab?.extensionId, tab?.connId, tab?.dbName, tab?.engine]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-establishes the extension's DB connection host-side, without the
  // extension iframe running: the saved connection config lives in the
  // extension's own storage file and the password (when remembered) in the
  // OS keychain — the same sources the extension itself reconnects from.
  const reconnect = useCallback(async () => {
    if (!tab || reconnecting) return;
    setReconnecting(true);
    setError(null);
    setFailedSql(null);
    try {
      // The sidebar extension may have auto-connected this connection already
      // — then Connect is just the user's opt-in for this tab, no need to
      // tear down and re-create the live pool.
      if (isDbConnectionLive(tab.extensionId, tab.connId)) {
        clearDbManagerTabNeedsReconnect(tab.id);
        return;
      }
      // Resolve this tab's connId against the multi-connection 'connections'
      // array (legacy single-object 'connectionConfig' fallback for restored
      // 'default' tabs is inside readSavedDbConnection).
      const saved = await readSavedDbConnection(tab.extensionId, tab.connId);
      if (!saved) {
        throw new Error('No saved connection found. Open the DB Manager panel and connect again.');
      }
      const engine = engineOf(tab);
      if ((DB_CONNECT_COMMANDS[saved.engine] ? saved.engine : 'mysql') !== engine) {
        throw new Error(
          `The saved connection is now ${DB_ENGINE_LABELS[saved.engine] || saved.engine}, but this tab needs ${DB_ENGINE_LABELS[engine]}. Reconnect from the DB Manager panel.`
        );
      }
      // One connect path, not a second copy of it.
      //
      // This used to build its own config and read the password straight from
      // the OS keychain — which stopped working the moment connections moved
      // into the vault, since a vault-stored password is not in the keychain.
      // It would have dialled with an empty one and shown whatever the server
      // said. connectDbFromSavedConfig knows about the vault, the TLS fields
      // and the password prompt; duplicating any of that is how the copies
      // drift apart.
      try {
        await connectDbFromSavedConfig(tab.extensionId, tab.connId);
      } catch (err) {
        if (String(err?.message) !== DB_PASSWORD_REQUIRED) throw err;
        const supplied = await new Promise((resolve) => {
          setPasswordPrompt({
            label: saved.name || saved.host || tab.connId,
            user: saved.user,
            submit: async ({ password, remember }) => {
              setPasswordPrompt(null);
              try {
                await connectDbFromSavedConfig(tab.extensionId, tab.connId, { password });
                if (remember && saved.origin === 'vault') {
                  // Only after a connect that worked — see the picker.
                  await setDbConnectionPassword(tab.connId, password).catch(() => {});
                }
                resolve(true);
              } catch (e2) {
                setError(e2?.message || String(e2));
                resolve(false);
              }
            },
            cancel: () => {
              setPasswordPrompt(null);
              resolve(false);
            },
          });
        });
        if (!supplied) return;
      }
      // Explicit user action on this tab — un-flag it (and only it); the
      // effect keyed on needsReconnect then loads the table.
      clearDbManagerTabNeedsReconnect(tab.id);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setReconnecting(false);
    }
  }, [tab, reconnecting, setError, setFailedSql, setPasswordPrompt]);

  return {
    reconnecting,
    copiedFormat,
    copyResult,
    selectResultSet,
    exportFetchChunk,
    querySchema,
    reconnect,
  };
}
